import { Button } from "@mui/material";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { FC, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useBreadcrumbs } from "shared/hooks/useBreadcrumbs";
import { useDynamicRouting } from "shared/hooks/useDynamicRouting";
import { ArrowBackIcon } from "shared/theme/icons/ArrowBack";
import Breadcrumbs from "../Breadcrumbs";
import { StyledWrapper } from "./styled";

type PageHeadingProps = {
  children?: ReactNode;
  maintenanceModeSwitch?: ReactNode;
  customPageTitle?: string;
  additionalLabel?: string;
  hasBackButton?: boolean;
};

const PageHeading: FC<PageHeadingProps> = ({
  children,
  customPageTitle,
  additionalLabel,
  hasBackButton = false,
  maintenanceModeSwitch,
}) => {
  const { dynamicRoutes } = useDynamicRouting();
  const { pathname: currentPath } = useLocation();
  const navigate = useNavigate();
  const breadcrumbs = useBreadcrumbs();

  const title = breadcrumbs.length
    ? breadcrumbs[breadcrumbs.length - 1].title
    : dynamicRoutes.find((route) => route.pathname === currentPath)?.title;

  const pageTitle = customPageTitle ?? title;

  const getBreadcrumbsList = () => {
    const isFreeToPlay =
      breadcrumbs.length > 0 && "isFreeToPlay" in breadcrumbs[0];

    if (isFreeToPlay) {
      return breadcrumbs;
    }

    if (customPageTitle && breadcrumbs.length > 1) {
      const lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1];

      return [
        ...breadcrumbs.slice(0, -1),
        {
          title: lastBreadcrumb.title + (additionalLabel ?? ""),
          pathname: currentPath.split("/").slice(0, -1).join("/"),
        },
        { title: customPageTitle },
      ];
    }

    return breadcrumbs;
  };

  return (
    <StyledWrapper>
      <Stack spacing="0.5rem" width="100%">
        <Breadcrumbs breadcrumbs={getBreadcrumbsList()} />
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box display="flex" alignItems="center">
            {hasBackButton && (
              <Button onClick={() => navigate(-1)}>
                <ArrowBackIcon />
              </Button>
            )}
            <Typography variant="heading1">{pageTitle}</Typography>
            {maintenanceModeSwitch && (
              <Box display="flex" ml={2} alignItems="center">
                {maintenanceModeSwitch}
              </Box>
            )}
          </Box>
          {children}
        </Box>
      </Stack>
    </StyledWrapper>
  );
};

export default PageHeading;
